import { Menu, MenuItem, Spinner } from "@blueprintjs/core";
import { ContextMenu2, Popover2 } from "@blueprintjs/popover2";
import queryString from "query-string";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import { useSynergyAuth } from "../../hooks/use-synergy-auth";
import { useUploadVideos } from "../../hooks/use-upload-videos";
import SynergySVG from "../../images/custom-icons/synergy";
import { playIcon, plusIcon } from "../../images/global";
import { AssetDTO, useUpdatePlayVideoMutation } from "../../redux/slices";
import { SportPlay } from "../../types";
import { PlayVideoOverlay } from "../play-video-overlay/play-video-overlay";
import { SynergyVideoDialog } from "../synergy-video-dialog/synergy-video-dialog";
import { PlayVideoContextMenu } from "./play-video-context-menu";
import styles from "./play-video-display.module.scss";
import SynergyAuth from "./synergy-auth";

export interface PlayVideoDisplayProps {
  play: SportPlay;
}

export const PlayVideoDisplay = ({ play }: PlayVideoDisplayProps) => {
  const [video, setVideo] = useState<AssetDTO | undefined>(undefined);
  const [uploadingVideo, setUploadingVideo] = useState<boolean>(false);
  const [showSynergyModal, setShowSynergyModal] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const synergyForm = useRef<HTMLFormElement>(null);

  const { uploadSynergyClips, uploadLocalVideos } = useUploadVideos({ playId: play.id, setUploadingVideo });
  const [updatePlayVideo] = useUpdatePlayVideoMutation();

  const { handleSynergyClick } = useSynergyAuth({ synergyForm, setShowSynergyModal });

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadLocalVideos(e.target.files);
    }

    (e.target as HTMLInputElement).value = "";
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpdateVideo = (video: AssetDTO) => {
    updatePlayVideo({ body: video, playId: play.id, assetId: video.id });
  };

  // if modal=synergy is in url open synergy modal and remove it from url
  useEffect(() => {
    const hashParams = queryString.parse(window.location.hash);
    const modal = hashParams.modal;

    if (modal === "synergy") {
      setShowSynergyModal(true);
      // remove modal param from url
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div className={styles.playVideoDisplay}>
      <PlayVideoOverlay video={video} setVideo={setVideo} updateVideo={handleUpdateVideo} />
      <input type="file" accept="video/*" ref={fileInputRef} style={{ display: "none" }} onChange={handleUpload} />
      <div className={styles.playVideoContainer}>
        <Popover2
          interactionKind="click"
          placement="auto"
          content={<VideoPopover clickInput={handleButtonClick} openSynergy={handleSynergyClick} />}
        >
          <div className={styles.greyRect}>
            <img className={styles.selectVideoButton} src={plusIcon} height={112} width={200} />
          </div>
        </Popover2>
        {play.assets.map((asset) => (
          <ContextMenu2 key={asset.id} content={<PlayVideoContextMenu video={asset} playId={play.id} />}>
            <div className={styles.container} onClick={() => setVideo(asset)}>
              <img src={asset.previewUrl} height={112} width={200} />
              <div className={styles.hoverOverlay}>
                <img className={styles.playIcon} src={playIcon} height={25} width={25} />
              </div>
            </div>
          </ContextMenu2>
        ))}
        {uploadingVideo && (
          <div className={styles.greyRect}>
            <Spinner />
          </div>
        )}
      </div>
      <SynergyAuth synergyForm={synergyForm} playId={play.id} />
      <SynergyVideoDialog
        open={showSynergyModal}
        onClose={() => setShowSynergyModal(false)}
        uploadClips={uploadSynergyClips}
      />
    </div>
  );
};

interface VideoPopoverProps {
  clickInput: () => void;
  openSynergy: () => void;
}

const VideoPopover = ({ clickInput, openSynergy }: VideoPopoverProps) => {
  return (
    <Menu>
      <MenuItem text="From Computer" icon="document" onClick={clickInput} />
      <MenuItem text={<SynergySVG width={120} />} onClick={openSynergy} />
    </Menu>
  );
};
